import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { CoachSettings, WebSettings } from "@/interfaces";
import { useSocialCoachSession } from "./useSocialCoachSession";
import { useAccountSettings } from "./useAccountSettings";
import { usePlayerSettings } from "./usePlayerSettings";
import {
  Blog,
  BlogServices,
  CancelRequestOptions,
  CreateBlogRequest,
  ExtraFieldsParam,
  GetBlogsFilters,
  GetBlogsParams,
  UpdateBlogRequest,
} from "@/services/bedrock";
import { useCallback, useMemo } from "react";
import { toast } from "sonner";

function blogExtraFieldsQueryKey(extraFields: ExtraFieldsParam | undefined): unknown {
  if (extraFields === undefined) return [];
  if (extraFields === true) return true;
  if (Array.isArray(extraFields)) return extraFields.slice().sort();
  const record = extraFields as Exclude<ExtraFieldsParam, true | readonly string[]>;
  return Object.keys(record)
    .sort()
    .map(key => [key, blogExtraFieldsQueryKey(record[key])] as const);
}

function buildBlogsQueryOptions(
  coachId: number | undefined,
  params: GetBlogsParams = {},
  options?: CancelRequestOptions
) {
  return {
    queryKey: [
      "blogsList",
      coachId,
      params.search,
      params.playerId,
      params.contentPageId,
      params.videoProjectId,
      params.status,
      blogExtraFieldsQueryKey(params.extraFields),
      params.page,
      params.perPage,
      params.sort,
      params.order,
    ] as const,
    queryFn: () => BlogServices.getBlogs(params, options),
    staleTime: 1000 * 60 * 5,
  };
}

export const useBlogSearch = () => {
  const { coachId } = useSocialCoachSession();
  const queryClient = useQueryClient();

  const invalidateBlogs = useCallback(
    () => queryClient.invalidateQueries({ queryKey: ["blogsList", coachId] }).catch(console.error),
    [coachId, queryClient]
  );

  // `enabled` lets callers keep this hook unconditional while skipping the request, e.g. when the
  // Coach does not have Blogs turned on.
  const searchBlogs = useCallback(
    (params: GetBlogsParams = {}, queryOptions?: { enabled?: boolean }) => {
      const options = buildBlogsQueryOptions(coachId, params);
      const {
        data: blogs,
        isFetching,
        isError,
      } = useQuery({
        ...options,
        enabled: queryOptions?.enabled ?? true,
        placeholderData: keepPreviousData,
      });
      return { blogs, isFetching, isError };
    },
    [coachId]
  );

  const fetchBlogs = useCallback(
    (params: GetBlogsParams = {}, options?: CancelRequestOptions) =>
      queryClient.fetchQuery(buildBlogsQueryOptions(coachId, params, options)),
    [coachId, queryClient]
  );

  const createBlogMutation = useMutation({
    mutationFn: (request: CreateBlogRequest) => BlogServices.createBlog(request),
    onSuccess: () => invalidateBlogs(),
    onError: (error: any) => {
      console.error(error);
      toast.error(error?.response?.data?.message || "Failed to start the blog. Please try again.");
    },
  });

  const updateBlogMutation = useMutation({
    mutationFn: ({ blogId, request }: { blogId: number; request: UpdateBlogRequest }) =>
      BlogServices.updateBlog(blogId, request),
    onSuccess: () => {
      toast.success("Blog saved");
      invalidateBlogs();
    },
    onError: error => {
      console.error(error);
      toast.error("Failed to save the blog");
    },
  });

  const publishBlogMutation = useMutation({
    mutationFn: (blogId: number) => BlogServices.publishBlog(blogId),
    onSuccess: () => {
      toast.success("Blog published");
      invalidateBlogs();
    },
    onError: (error: any) => {
      console.error(error);
      toast.error(error?.response?.data?.message || "Failed to publish the blog");
    },
  });

  const unpublishBlogMutation = useMutation({
    mutationFn: (blogId: number) => BlogServices.unpublishBlog(blogId),
    onSuccess: () => {
      toast.success("Blog unpublished");
      invalidateBlogs();
    },
    onError: error => {
      console.error(error);
      toast.error("Failed to unpublish the blog");
    },
  });

  const regenerateBlogMutation = useMutation({
    mutationFn: (blogId: number) => BlogServices.regenerateBlog(blogId),
    onSuccess: () => invalidateBlogs(),
    onError: (error: any) => {
      console.error(error);
      toast.error(error?.response?.data?.message || "Failed to regenerate the blog");
    },
  });

  const archiveBlogMutation = useMutation({
    mutationFn: (blogId: number) => BlogServices.archiveBlog(blogId),
    onSuccess: () => {
      toast.success("Blog deleted");
      invalidateBlogs();
    },
    onError: error => {
      console.error(error);
      toast.error("Failed to delete the blog");
    },
  });

  return useMemo(
    () => ({
      searchBlogs,
      fetchBlogs,
      invalidateBlogs,
      createBlogMutation,
      updateBlogMutation,
      publishBlogMutation,
      unpublishBlogMutation,
      regenerateBlogMutation,
      archiveBlogMutation,
    }),
    [
      searchBlogs,
      fetchBlogs,
      invalidateBlogs,
      createBlogMutation,
      updateBlogMutation,
      publishBlogMutation,
      unpublishBlogMutation,
      regenerateBlogMutation,
      archiveBlogMutation,
    ]
  );
};

/** Coaches use the BLOGS account setting; Players use PLAYER_BLOG_ACCESS (which bedrock also gates on BLOGS). */
export const useHasBlogAccess = (playerId?: number | string | null) => {
  const { isPlayer } = useSocialCoachSession();
  const { hasSetting } = usePlayerSettings(playerId);
  const { hasAccountSetting } = useAccountSettings();
  return !isPlayer ? hasAccountSetting(WebSettings.BLOGS) : !!hasSetting(CoachSettings.PLAYER_BLOG_ACCESS);
};

export const useExistingBlog = (
  filters: Pick<GetBlogsFilters, "contentPageId" | "videoProjectId">,
  enabled: boolean
): { existingBlog: Blog | undefined; loadingExistingBlog: boolean } => {
  const { searchBlogs } = useBlogSearch();
  const { blogs, isFetching } = searchBlogs(
    { ...filters, perPage: 1, sort: "created_date", order: "desc" },
    { enabled }
  );
  return { existingBlog: blogs?.content?.[0], loadingExistingBlog: isFetching };
};
